/**
 * vendor/isomorphic/apps/isomorphic/src/app/shared/job-board/dashboard/top-referrers.tsx — its
 * `SingleBar` rows (label, then a bar with the figure beside it) inside a `WidgetCard`, fed by
 * `getOrderPipeline()`'s `{state: count}` map instead of the template's constants.
 *
 * This replaced a donut. Twelve lifecycle states are an ordered sequence, not parts of a whole a
 * reader compares by angle, and a donut past six slices stops being readable (dataviz skill,
 * anti-patterns). So: one row per stage in lifecycle order, one hue, cancelled in grey. Each row is
 * a link to the orders list filtered to that stage (FR-007), so the stage name, never the colour,
 * carries identity (SC-009).
 *
 * The count printed on each row is the API's own. The bar's length is that count drawn against the
 * largest stage — a proportion of pixels for the eye, not a figure anyone reads; nothing is summed
 * or re-grouped (constitution I), which is also why there is no "Total" row.
 */
import { Link } from "react-router-dom";
import { PiPackageDuotone as Package } from "react-icons/pi";
import { WidgetCard, cn } from "@steelyard/theme";
import { TileBoundary } from "./TileBoundary";
import { EmptyTile, UnreadableWidgetCard, formatCount } from "./TrendStatCard";
import { INERT, STEEL } from "./palette";
import { ORDER_STATES, money } from "../../lib/services/ordering";

function unreadable(error: unknown) {
  return <UnreadableWidgetCard title="Orders by status" error={error} fallback="Could not be read — this is not the same as no orders." />;
}

export function OrdersByStatusTile({ resolve, className }: { resolve: Promise<Record<string, number | string>>; className?: string }) {
  return (
    <TileBoundary resolve={resolve} errorFallback={unreadable} fallbackClassName="h-[420px]">
      {(pipeline) => {
        // Lifecycle order from ORDER_STATES, not the map's key order; `cart` never holds an order.
        const rows = ORDER_STATES.flatMap(({ state, label }) => {
          const count = money(pipeline[state]);
          return count !== null && count > 0 ? [{ state, label, count }] : [];
        });
        const largest = Math.max(1, ...rows.map((row) => row.count));
        return (
          <WidgetCard
            title="Orders by status"
            description="Every order by where it is now. Select a stage to open those orders."
            descriptionClassName="text-gray-500 mt-1"
            className={cn("flex flex-col", className)}
          >
            {rows.length === 0 ? (
              <EmptyTile icon={<Package />} title="No orders yet">
                Orders appear here by status as customers place them.
              </EmptyTile>
            ) : (
              <ul className="mt-5 flex grow flex-col gap-1">
                {rows.map((row) => (
                  <li key={row.state}>
                    {/* One line per stage — name, bar, count — so ten stages fit beside the banner and
                        stat row instead of stretching them. */}
                    <Link
                      to={`/orders?state=${encodeURIComponent(row.state)}`}
                      className="group -mx-2 grid grid-cols-[minmax(0,9rem)_1fr_2.5rem] items-center gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                    >
                      <span className="truncate font-medium text-gray-700 group-hover:text-gray-900">{row.label}</span>
                      <span aria-hidden className="block h-2 rounded-full bg-gray-100">
                        <span
                          className="block h-2 rounded-full"
                          style={{ width: `${(row.count / largest) * 100}%`, backgroundColor: row.state === "cancelled" ? INERT : STEEL }}
                        />
                      </span>
                      <span className="text-end font-semibold tabular-nums text-gray-900">{formatCount(row.count)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </WidgetCard>
        );
      }}
    </TileBoundary>
  );
}
